import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, CircularProgress,
  Chip, MenuItem, Switch, FormControlLabel, Tooltip, Avatar, InputAdornment
} from '@mui/material';
import {
  Add, Edit, PowerSettingsNew, Person,
  TableRows, GridView, Search
} from '@mui/icons-material';
import { getUsers, createUser, updateUser, toggleUserActive, UserResponse, UserRequest } from '../../api/users';

// ─── Tasarım sabitleri ────────────────────────────────────────────────────────
const BRAND   = '#1A3A6B';
const ACCENT  = '#c0392b';
const BG      = '#f5f6fa';
const SURFACE = '#ffffff';
const BORDER  = '#e8eaf0';
const TEXT2   = '#8a92a6';

const AVATAR_PALETTE = ['#6366f1','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#ef4444'];
const avatarColor = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};
const getInitials = (name: string) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const emptyForm: Partial<UserRequest> = {
  username: '', password: '', email: '', fullName: '', role: 'USER', active: true
};

// ─── Badge'ler ────────────────────────────────────────────────────────────────
const RoleBadge: React.FC<{ role: string }> = ({ role }) => (
    <Chip label={role === 'ADMIN' ? 'Admin' : 'Kullanıcı'} size="small" sx={{
      height: 20, fontSize: '11px', fontWeight: 700, borderRadius: '20px',
      bgcolor: role === 'ADMIN' ? '#fde8e8' : '#eff6ff',
      color: role === 'ADMIN' ? ACCENT : '#2563eb',
      border: 'none',
    }} />
);

const StatusBadge: React.FC<{ active: boolean }> = ({ active }) => (
    <Chip label={active ? 'Aktif' : 'Pasif'} size="small" sx={{
      height: 20, fontSize: '11px', fontWeight: 700, borderRadius: '20px',
      bgcolor: active ? '#eafaf1' : '#f4f5f7',
      color: active ? '#1e8449' : '#9ca3af',
      border: 'none',
    }} />
);

// ─── Kullanıcı Kartı ──────────────────────────────────────────────────────────
const UserCard: React.FC<{
  user: UserResponse;
  onEdit: () => void;
  onToggle: () => void;
}> = ({ user, onEdit, onToggle }) => {
  const color = avatarColor(user.fullName);
  return (
      <Box sx={{
        bgcolor: SURFACE, borderRadius: '10px', p: 2.25,
        border: `1px solid ${BORDER}`,
        display: 'flex', gap: 1.75, alignItems: 'flex-start',
        opacity: user.active ? 1 : 0.6,
        transition: 'box-shadow .15s, transform .15s',
        '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,.09)', transform: 'translateY(-1px)' },
      }}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: color, fontSize: '14px', fontWeight: 700, borderRadius: '12px', flexShrink: 0 }}>
          {getInitials(user.fullName)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1a2535', lineHeight: 1.2 }}>
              {user.fullName}
            </Typography>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: user.active ? '#10b981' : '#d1d5db', flexShrink: 0 }} />
          </Box>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '11px', color: TEXT2, mb: 0.5 }}>
            @{user.username}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mb: 1.25 }}>
            {user.email}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <RoleBadge role={user.role} />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Düzenle">
                <IconButton size="small" onClick={onEdit} sx={actionBtn}>
                  <Edit sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={user.active ? 'Pasife Al' : 'Aktife Al'}>
                <IconButton size="small" onClick={onToggle} sx={{ ...actionBtn, opacity: user.active ? 1 : 0.4 }}>
                  <PowerSettingsNew sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
  );
};

const actionBtn = {
  width: 26, height: 26, borderRadius: '6px',
  border: `1px solid ${BORDER}`, color: '#64748b',
  '&:hover': { bgcolor: '#f0f4ff', color: BRAND, borderColor: '#c7d2fe' },
};

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────
const AdminPage: React.FC = () => {
  const [users, setUsers]   = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen]     = useState(false);
  const [editing, setEditing] = useState<UserResponse | null>(null);
  const [form, setForm]     = useState<Partial<UserRequest>>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [view, setView]     = useState<'table' | 'card'>('table');
  const [search, setSearch] = useState('');

  const load = async () => {
    try { setLoading(true); setUsers(await getUsers()); }
    catch { setError('Kullanıcılar yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = users.filter(u =>
      u.fullName.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.username.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = (user?: UserResponse) => {
    setEditing(user || null);
    setForm(user ? { username: user.username, email: user.email, fullName: user.fullName, role: user.role, active: user.active, password: '' } : emptyForm);
    setOpen(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.fullName || !form.email || (!editing && !form.password)) {
      setError('Ad Soyad, E-posta ve Şifre zorunludur'); return;
    }
    setSaving(true);
    try {
      if (editing) { await updateUser(editing.id, form); setSuccess('Kullanıcı güncellendi'); }
      else          { await createUser(form);             setSuccess('Kullanıcı oluşturuldu'); }
      setOpen(false); load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Kayıt sırasında hata oluştu');
    } finally { setSaving(false); }
  };

  const handleToggle = async (id: number, name: string) => {
    try { await toggleUserActive(id); setSuccess(`${name} güncellendi`); load(); }
    catch { setError('Güncelleme hatası'); }
  };

  const admins = users.filter(u => u.role === 'ADMIN').length;
  const active = users.filter(u => u.active).length;

  return (
      <Box sx={{ bgcolor: BG, minHeight: '100vh', p: 3 }}>

        {/* ── Topbar ── */}
        <Box sx={{ bgcolor: SURFACE, borderBottom: `1px solid ${BORDER}`, mx: -3, mt: -3, px: 3, py: 0, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: TEXT2, fontSize: '13px' }}>
            <Typography sx={{ fontSize: '13px', color: TEXT2 }}>Yönetim</Typography>
            <Typography sx={{ color: BORDER }}>/</Typography>
            <Typography sx={{ fontSize: '13px', color: '#1a2535', fontWeight: 500 }}>Kullanıcılar</Typography>
          </Box>
          <Box />
        </Box>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#1a2535', lineHeight: 1.2 }}>
              Kullanıcı Yönetimi
            </Typography>
            <Box sx={{ display: 'flex', gap: 2.5, mt: 0.75 }}>
              {[[users.length,'kullanıcı'],[admins,'admin'],[active,'aktif']].map(([n,l])=>(
                  <Typography key={String(l)} variant="body2" sx={{ color: TEXT2, fontSize: '13px' }}>
                    <Box component="span" sx={{ fontWeight: 700, color: '#1a2535' }}>{n}</Box> {l}
                  </Typography>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Arama */}
            <TextField
                size="small" placeholder="Ara..." value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: TEXT2 }} /></InputAdornment>,
                  sx: { borderRadius: '8px', fontSize: '13px', bgcolor: SURFACE, width: 200 }
                }}
                sx={{ '& fieldset': { borderColor: BORDER } }}
            />

            {/* Görünüm toggle */}
            <Box sx={{ display: 'flex', border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden', bgcolor: SURFACE }}>
              {(['table','card'] as const).map((v, i) => (
                  <Box key={v} onClick={() => setView(v)} sx={{
                    width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: view === v ? BRAND : TEXT2,
                    bgcolor: view === v ? '#eff6ff' : 'transparent',
                    borderRight: i === 0 ? `1px solid ${BORDER}` : 'none',
                    transition: 'all .15s',
                  }}>
                    {v === 'table' ? <TableRows sx={{ fontSize: 17 }} /> : <GridView sx={{ fontSize: 17 }} />}
                  </Box>
              ))}
            </Box>

            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()} sx={{
              bgcolor: ACCENT, fontWeight: 600, borderRadius: '8px', px: 2, fontSize: '13px', boxShadow: 'none', height: 36,
              '&:hover': { bgcolor: '#a93226', boxShadow: 'none' },
            }}>
              Yeni Kullanıcı
            </Button>
          </Box>
        </Box>

        {/* ── Bildirimler ── */}
        {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* ── İçerik ── */}
        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <CircularProgress sx={{ color: BRAND }} />
            </Box>
        ) : view === 'table' ? (

            /* ── Tablo görünümü ── */
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '10px', border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fc' }}>
                    {['Kullanıcı','Kullanıcı Adı','E-posta','Rol','Durum','Kayıt','İşlem'].map(h => (
                        <TableCell key={h} align={h==='İşlem'?'center':'left'} sx={{
                          py: 1.5, fontSize: '11px', fontWeight: 600,
                          color: TEXT2, textTransform: 'uppercase', letterSpacing: '0.05em',
                          borderBottom: `1px solid ${BORDER}`,
                        }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map(user => (
                      <TableRow key={user.id} sx={{
                        opacity: user.active ? 1 : 0.55,
                        '&:hover': { bgcolor: '#fafbfe' },
                        '& td': { borderBottom: `1px solid ${BORDER}`, py: 0 },
                      }}>
                        <TableCell sx={{ height: 60 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 32, height: 32, fontSize: '11px', fontWeight: 700, bgcolor: avatarColor(user.fullName) }}>
                              {getInitials(user.fullName)}
                            </Avatar>
                            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1a2535' }}>{user.fullName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell><Typography sx={{ fontSize: '12px', color: TEXT2, fontFamily: 'monospace' }}>{user.username}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '12px', color: '#6b7280' }}>{user.email}</Typography></TableCell>
                        <TableCell><RoleBadge role={user.role} /></TableCell>
                        <TableCell><StatusBadge active={user.active} /></TableCell>
                        <TableCell><Typography sx={{ fontSize: '12px', color: TEXT2 }}>{user.createdAt ? new Date(user.createdAt).toLocaleDateString('tr-TR') : '—'}</Typography></TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Düzenle">
                              <IconButton size="small" onClick={() => handleOpen(user)} sx={actionBtn}><Edit sx={{ fontSize: 14 }} /></IconButton>
                            </Tooltip>
                            <Tooltip title={user.active ? 'Pasife Al' : 'Aktife Al'}>
                              <IconButton size="small" onClick={() => handleToggle(user.id, user.fullName)} sx={{ ...actionBtn, opacity: user.active ? 1 : 0.4 }}>
                                <PowerSettingsNew sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

        ) : (

            /* ── Kart görünümü ── */
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.75 }}>
              {filtered.map(user => (
                  <UserCard key={user.id} user={user}
                            onEdit={() => handleOpen(user)}
                            onToggle={() => handleToggle(user.id, user.fullName)} />
              ))}
            </Box>
        )}

        {/* ── Dialog ── */}
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
          <DialogTitle sx={{ bgcolor: BRAND, color: '#fff', py: 2, px: 3, fontSize: '15px', fontWeight: 600 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person fontSize="small" />
              {editing ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, px: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
              {[
                { label: 'Ad Soyad *', key: 'fullName', autoFocus: true },
                { label: 'Kullanıcı Adı', key: 'username', disabled: !!editing },
                { label: 'E-posta *', key: 'email' },
                { label: editing ? 'Yeni Şifre (boş = değişmez)' : 'Şifre *', key: 'password', type: 'password' },
              ].map(({ label, key, autoFocus, disabled, type }) => (
                  <TextField key={key} label={label} type={type || 'text'}
                             value={(form as any)[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })}
                             fullWidth autoFocus={autoFocus} disabled={disabled} size="small"
                             sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
              ))}
              <TextField select label="Rol" value={form.role || 'USER'}
                         onChange={e => setForm({ ...form, role: e.target.value })}
                         fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                <MenuItem value="USER">Kullanıcı</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
              </TextField>
              {editing && (
                  <FormControlLabel
                      control={<Switch checked={form.active || false} onChange={e => setForm({ ...form, active: e.target.checked })} />}
                      label={<Typography sx={{ fontSize: '13px' }}>Aktif</Typography>} />
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button onClick={() => setOpen(false)} sx={{ borderRadius: '8px', color: TEXT2, textTransform: 'none' }}>İptal</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{
              bgcolor: BRAND, borderRadius: '8px', fontWeight: 600, textTransform: 'none', px: 3, boxShadow: 'none',
              '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' },
            }}>
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Kaydet'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default AdminPage;
